import React, { useMemo, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Grid, TextField, RadioGroup, FormControlLabel, Radio, Divider } from '@mui/material';
import usersService from '../../axios/usersService';

const scores = Array.from({ length: 11 }, (_, i) => i);

export default function NPSPopup({ open, onClose, onSubmitted }) {
  const [score, setScore] = useState(null);
  const [relativeRank, setRelativeRank] = useState('');
  const [dataFeedInterest, setDataFeedInterest] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState(false);

  const isValid = useMemo(() => score !== null && relativeRank && dataFeedInterest, [score, relativeRank, dataFeedInterest]);

  const handleSubmit = async () => {
    setTouched(true);
    if (!isValid) return;
    setSubmitting(true);
    try {
      const responses = [
        { nps_question_id: 1, response: score.toString() },
        { nps_question_id: 2, response: relativeRank },
        { nps_question_id: 3, response: dataFeedInterest }
      ];
      await usersService.submitNpsResponse(responses);
      onSubmitted?.();
      onClose?.();
    } catch (e) {
      // Swallow error; modal will close only if succeed
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown
      disableBackdropClick
      onClose={(event, reason) => {
        // prevent closing without submit
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
        // allow programmatic close after submit
        onClose?.();
      }}
    >
      <DialogTitle>We value your feedback</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2 }}>
          Please take a couple of minutes to fill out this quick user survey.
        </Typography>

        <Box sx={{ mb: 4 }}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>
            1. On a scale of 0 to 10, how likely are you to recommend US Food’s Shared Table supplier data platform to a friend or colleague?
          </Typography>
          <Typography variant="body2">0 = Not at all likely</Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>10 = Extremely likely</Typography>
          <Grid container spacing={1}>
            {scores.map((s) => (
              <Grid item xs={12/11} key={s}>
                <Button
                  variant={score === s ? 'contained' : 'outlined'}
                  onClick={() => setScore(s)}
                  fullWidth
                >
                  {s}
                </Button>
              </Grid>
            ))}
          </Grid>
          {touched && score === null && (
            <Typography color="error" variant="caption">This question is required.</Typography>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 4 }}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>
            2. Relative to other supplier data platforms in the broadline distribution industry, how do you rank Shared Table versus other similar data solutions?
          </Typography>
          <RadioGroup value={relativeRank} onChange={(e) => setRelativeRank(e.target.value)}>
            <FormControlLabel value="Better than other data solutions" control={<Radio />} label="A. Better than other data solutions" />
            <FormControlLabel value="About the same as other data solutions" control={<Radio />} label="B. About the same as other data solutions" />
            <FormControlLabel value="Worse than other data solutions" control={<Radio />} label="C. Worse than other data solutions" />
            <FormControlLabel value="Not applicable; only familiar with Shared Table" control={<Radio />} label="D. Not applicable; only familiar with Shared Table" />
          </RadioGroup>
          {touched && !relativeRank && (
            <Typography color="error" variant="caption">This question is required.</Typography>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 4 }}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>
            3. Would your organization be interested in getting a Shared Table direct data feed?
          </Typography>
          <RadioGroup value={dataFeedInterest} onChange={(e) => setDataFeedInterest(e.target.value)}>
            <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
            <FormControlLabel value="No" control={<Radio />} label="No" />
            <FormControlLabel value="I'm not sure" control={<Radio />} label="I'm not sure" />
          </RadioGroup>
          {touched && !dataFeedInterest && (
            <Typography color="error" variant="caption">This question is required.</Typography>
          )}
        </Box>

        <Typography variant="body2">Thank you for taking the time to submit your feedback!</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSubmit} disabled={!isValid || submitting} variant="contained" color="primary">
          {submitting ? 'Submitting...' : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
